import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { mdiCheckCircleOutline, mdiAlert } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Modal from "components/Modal";
import Stepper from "components/Stepper";
import { PERCENTAGE_LIST } from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import { getDefaultContractType } from "helpers/convertFormData";
import { showErrorMessage, showWarningMessage } from "helpers/toastMessage";
import { renderApiErrorMessage } from "helpers/renderApiError";
import { IOption } from "models/Form";
import {
  ICompany,
  IFundingRequestForm,
  IFundingRequestLineItems,
  ILineList,
  IPaymentProfile
} from "models/HaulPay";
import FundingRequestInfo from "./FundingRequestInfo";
import LoadInfo from "./LoadInfo";
import {
  MUTATION_CREATE_BROKER_FUNDING_REQUEST,
  MUTATION_CREATE_DIRECT_BROKER_FUNDING_REQUEST
} from "./query";
import styles from "./style.module.scss";
import { BrokerProvider } from "context/BrokerContext";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";
import { selectUserPermission } from "store/auth/selector";
import { selectFactoringProfile } from "store/user/selector";
import UploadDocuments from "./UploadDocuments";
import dollarsToCents from "helpers/dollarsToCents";

interface ISectionProps {
  activeStep: {
    id: number;
    data: {
      fundingRequestId?: string;
      contractType?: string;
    };
  };
  companyValue?: ICompany;
  setStepCallback: (step: number, active: boolean) => void;
  fundingRequestFormData?: IFundingRequestForm;
  selectedPaymentProfile?: IPaymentProfile;
  setIsShowSearch: (show: boolean) => void;
  setFormDataCallback: (data: IFundingRequestForm) => void;
  setSelectedPaymentProfile: (item: IPaymentProfile) => void;
  createFundingRequest: (frInfo: IFundingRequestForm, lineItems: ILineList[]) => Promise<void>;
}

function SelectedSection({
  activeStep,
  companyValue,
  setStepCallback,
  setSelectedPaymentProfile,
  fundingRequestFormData,
  selectedPaymentProfile,
  setIsShowSearch,
  setFormDataCallback,
  createFundingRequest
}: Readonly<ISectionProps>) {
  switch (activeStep?.id) {
    case 0:
      return (
        <LoadInfo
          fundingRequestFormData={fundingRequestFormData}
          handleNext={() => setStepCallback(1, true)}
          setFormDataCallback={setFormDataCallback}
        />
      );

    case 1:
      return (
        <FundingRequestInfo
          companyValue={companyValue}
          selectedPaymentProfile={selectedPaymentProfile}
          setStepCallback={() => setStepCallback(0, true)}
          setIsShowSearch={setIsShowSearch}
          setFormDataCallback={setFormDataCallback}
          setSelectedPaymentProfile={setSelectedPaymentProfile}
          fundingRequestFormData={fundingRequestFormData}
          createFundingRequest={createFundingRequest}
        />
      );

    case 2:
      return (
        <UploadDocuments
          fundingRequestId={activeStep?.data?.fundingRequestId}
          contractType={activeStep?.data?.contractType}
        />
      );
    default:
      return null;
  }
}

export default function CreateFundingRequest() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { targetRate } = state || {};
  const userPermission = useSelector(selectUserPermission);
  const factoringProfile = useSelector(selectFactoringProfile);
  const defaultContractType = getDefaultContractType(
    userPermission?.available_contract_types,
    factoringProfile?.default_fr_type_api
  );
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [companyValue] = useState<ICompany>();
  const [selectedPaymentProfile, setSelectedPaymentProfile] = useState<IPaymentProfile>();
  const [fundingRequestFormData, setFundingRequestFormData] = useState<IFundingRequestForm>({
    lineItems: [] as IFundingRequestLineItems[],
    contractType: defaultContractType,
    originLocations: [
      {
        origin: null,
        originName: "",
        originInstruction: ""
      }
    ],
    destinationLocations: [
      {
        destination: null,
        destinationName: "",
        destinationInstruction: ""
      }
    ],
    paymentProfilePaymentMethod: "ACH",
    payoutDays: CARRIER_PAYMENT_SPEED[0] as IOption,
    factoringFeeSplitRate: PERCENTAGE_LIST[0] as IOption,
    paymentProfileAmount: targetRate / 100 || 0,
    pickUpOpenDate: dayjs().format("MM/DD/YYYY HH:mm"),
    pickUpCloseDate: dayjs().add(1, "day").format("MM/DD/YYYY HH:mm"),
    deliveryByDate: dayjs().add(4, "day").format("MM/DD/YYYY HH:mm")
  } as IFundingRequestForm);
  const [isSendAnyWaysModalOpen, setSendAnysWaysModal] = useState({
    isOpen: false,
    handleSendAnyway: () => {},
    handleCloseButton: () => {}
  });
  const [requestPaySteps, setRequestPaySteps] = useState([
    { id: 0, label: "Load Info", active: !state, data: {} },
    {
      id: 1,
      label: "Funding Info",
      active: !!state,
      data: {}
    },
    { id: 2, label: "Documents", active: false, data: {} }
  ]);
  const activeStep = requestPaySteps.find((step) => step.active) ?? requestPaySteps[0];
  const updateStep = (index: number, active: boolean, data: any = {}) => {
    setRequestPaySteps((prevSteps) => {
      const resetSteps = prevSteps.map((step) => ({ ...step, active: false }));
      resetSteps[index] = {
        ...resetSteps[index],
        active,
        data
      };
      return resetSteps;
    });
  };

  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();

  const [createBrokerFR] = useMutation(MUTATION_CREATE_BROKER_FUNDING_REQUEST, {
    onError(err) {
      showErrorMessage(renderApiErrorMessage(err));
    }
  });

  const [createBrokerDirectFR] = useMutation(MUTATION_CREATE_DIRECT_BROKER_FUNDING_REQUEST, {
    onError(err) {
      showErrorMessage(renderApiErrorMessage(err));
    }
  });

  const setFormDataCallback = (data: IFundingRequestForm) => {
    if (!data.paymentProfileAmount) {
      data.paymentProfileAmount = data.targetRate;
    }
    setFundingRequestFormData(data);
  };

  const handleSuccessModalClose = () => {
    toggleSuccessModal();
  };

  const createFundingRequest = async (frInfo: IFundingRequestForm, lineItems: ILineList[]) => {
    const originLocationArr = fundingRequestFormData?.originLocations?.map((item) => ({
      name: item.originName,
      streetOne: item.origin?.address,
      city: item.origin?.city,
      state: item.origin?.state,
      country: item.origin?.country,
      instructions: item.originInstruction
    }));

    const destinationLocationArr = fundingRequestFormData?.destinationLocations?.map((item) => ({
      name: item.destinationName,
      streetOne: item.destination?.address,
      city: item.destination?.city,
      state: item.destination?.state,
      country: item.destination?.country,
      instructions: item.destinationInstruction
    }));
    const paymentProfilePaymentMethod = frInfo?.paymentProfilePaymentMethod ?? "ach";
    const formData = {
      amount: dollarsToCents(frInfo.amount),
      debtor: frInfo.debtor?.id ?? "debdebde-debd-debd-debd-111111111111",
      ...(state
        ? {
            loadId: state.loadId,
            loadLength: state.loadLength,
            userLoadNumber: frInfo.userLoadNumber
          }
        : {
            originLocations: originLocationArr,
            destinationLocations: destinationLocationArr,
            loadLength: fundingRequestFormData?.loadLength,
            userLoadNumber: frInfo.userLoadNumber,
            loadWeight: fundingRequestFormData?.loadWeight,
            trailerType: fundingRequestFormData?.equipmentType?.value,
            timeStart: dayjs(fundingRequestFormData.pickUpOpenDate).format("YYYY-MM-DDTHH:mm:ssZ"),
            timeEnd: dayjs(fundingRequestFormData.pickUpCloseDate).format("YYYY-MM-DDTHH:mm:ssZ"),
            timeDelivery: dayjs(fundingRequestFormData.deliveryByDate).format(
              "YYYY-MM-DDTHH:mm:ssZ"
            ),
            noLoad: true
          }),
      paymentProfilePaymentMethod: (paymentProfilePaymentMethod as string).toLowerCase(),
      lineItems: lineItems,
      contractType: frInfo.contractType.value,
      carrierProNumber: frInfo.carrierProNumber,
      purchaseOrderNumber: frInfo.purchaseOrderNumber,
      paymentProfileAmount: dollarsToCents(frInfo.paymentProfileAmount),
      additionalDiscountRate: frInfo.debtor?.additional_discount_rate,
      ...(() => {
        if (
          ![
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT,
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD
          ].includes(frInfo.contractType.value)
        ) {
          return {
            paymentProfileId: selectedPaymentProfile?.id,
            payoutDays: frInfo.payoutDays?.value ?? CARRIER_PAYMENT_SPEED[0].value,
            factoringFeeSplitRate:
              frInfo.factoringFeeSplitRate?.value ??
              fundingRequestFormData.factoringFeeSplitRate?.value,
            applyReserveHoldback: frInfo.marginAdvance,
            factoringAmount:
              dollarsToCents(frInfo.amount) - dollarsToCents(frInfo.paymentProfileAmount)
          };
        } else if (
          frInfo.contractType.value === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD
        ) {
          return {
            paymentProfileId: selectedPaymentProfile?.id,
            nonFactoredType: "standard"
          };
        } else if (
          frInfo.contractType.value === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
        ) {
          return {
            nonFactoredType: "bill_out"
          };
        }
      })()
    };

    let result = true;
    if (
      formData.amount >= frInfo.debtor?.credit_available &&
      formData.contractType == FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER
    ) {
      result = await new Promise((resolve) => {
        setSendAnysWaysModal({
          isOpen: true,
          handleSendAnyway: () => {
            resolve(true);
            setSendAnysWaysModal((prevState) => ({ ...prevState, isOpen: false }));
          },
          handleCloseButton: () => {
            resolve(false);
            setSendAnysWaysModal((prevState) => ({ ...prevState, isOpen: false }));
          }
        });
      });
    }

    if (result) {
      try {
        const res = state
          ? await createBrokerFR({
              variables: { ...formData }
            })
          : await createBrokerDirectFR({
              variables: { ...formData }
            });
        if (!res?.errors) {
          const warning =
            res?.data?.createOrUpdateBrokerFundingRequest?.fundingRequest?.warnings
              ?.negative_margin;
          if (warning) {
            showWarningMessage(warning);
          }
          toggleSuccessModal();
          updateStep(2, true, {
            fundingRequestId: res?.data?.createOrUpdateBrokerFundingRequest?.fundingRequest.id,
            contractType: res?.data?.createOrUpdateBrokerFundingRequest?.fundingRequest.contractType
          });
        }
      } catch {
        showErrorMessage("Error creating funding request.");
      }
    }
  };
  return (
    <BrokerProvider>
      <div className={clsx(styles.createFRContainer, isShowSearch && "hidden")}>
        {!state ? (
          <div className={styles.body}>
            <h4 className={styles.subtitle}>{activeStep?.label}</h4>
            <Stepper
              data={requestPaySteps}
              currentStep={activeStep?.id}
              setStep={(index: number) => updateStep(index, true)}
              className={styles.stepper}
              canNavigate={false}
            />
          </div>
        ) : null}

        <SelectedSection
          companyValue={companyValue}
          setStepCallback={updateStep}
          setIsShowSearch={setIsShowSearch}
          fundingRequestFormData={fundingRequestFormData}
          selectedPaymentProfile={selectedPaymentProfile}
          setFormDataCallback={setFormDataCallback}
          setSelectedPaymentProfile={setSelectedPaymentProfile}
          createFundingRequest={createFundingRequest}
          activeStep={activeStep}
        />
      </div>

      <Modal
        isOpen={isSuccessModalOpen}
        closeModal={() =>
          navigate(routes.HAULPAY_BROKER_MANAGE_PAY, {
            replace: true,
            state: {
              forceUpdate: true
            }
          })
        }
        wrapperClass="!my-auto desktop:!min-h-fit text-sm"
        dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <div className="flex gap-1 text-xl font-bold text-success">
          <Icon path={mdiCheckCircleOutline} />
          <span>Success</span>
        </div>
        <p className="flex flex-col gap-3 px-6">Successfully made payment request.</p>
        <Button
          type="button"
          onClick={handleSuccessModalClose}
          className="self-end w-20 rounded-full h-9">
          OK
        </Button>
      </Modal>

      <Modal
        isOpen={isSendAnyWaysModalOpen.isOpen}
        closeModal={isSendAnyWaysModalOpen.handleCloseButton}
        wrapperClass="!my-auto desktop:!min-h-fit text-sm"
        dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <div className="flex gap-1 text-xl font-bold text-warning">
          <Icon path={mdiAlert} />
          <span>Warning</span>
        </div>
        <p className="flex flex-col gap-3 px-6">
          The amount requested is more than the credit limit set for this customer.
        </p>
        <Button
          type="button"
          onClick={isSendAnyWaysModalOpen.handleSendAnyway}
          className="self-end rounded-full w-30 h-9">
          Send Anyways
        </Button>
      </Modal>
    </BrokerProvider>
  );
}
